import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FaBars, FaTimes, FaRegUser, FaArrowRight, FaChevronRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";
import { clearUser } from "../../redux/slices/userSlice";
import DropdownUser from "../dashboardLyout/Header/DropdownUser";
import logo from "../../assest/logo.png";

/* ------------------------------------------------------------------ */
/*  Full-Width Edge-to-Edge Header with Glassmorphism & Navigation   */
/* ------------------------------------------------------------------ */

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { userLoginToken } = useSelector((state) => state.user);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 25);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent the page behind the mobile drawer from scrolling — on small
  // screens the background content was bleeding through / shifting while
  // the drawer was open, which is what was causing the overlapping look.
  useEffect(() => {
    if (isMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMenuOpen]);

  const handleLogout = () => {
    dispatch(clearUser());
    navigate("/");
  };

  const navLinks = [
    { name: "Home", to: "homeID", offset: -30 },
    { name: "About", to: "aboutID", offset: -80 },
    { name: "Services", to: "servicesID", offset: -40 },
    { name: "Features", to: "featuresID", offset: -40 },
    { name: "Our Vision", to: "ourVisionID", offset: -80 },
    { name: "Blog", to: "blogID", offset: -80 },
    { name: "Contact", to: "contactID", offset: -30 },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#F0DCC8] shadow-[0_10px_30px_-10px_rgba(28,20,16,0.08)] py-2.5 sm:py-3"
          : "bg-[#FFFBF5]/90 backdrop-blur-md border-b border-[#F5DFC5] py-3.5 sm:py-4.5"
      }`}
    >
      {/* Full width alignment container matching all page sections */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl flex items-center justify-between">
        {/* ================ LOGO ZONE ================ */}
        <div
          className="flex items-center gap-3 cursor-pointer group"
          onClick={() => navigate("/")}
        >
          <img
            width={140}
            src={logo}
            alt="MyAd360 Logo"
            className="h-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* ================ DESKTOP CENTER NAVIGATION ================ */}
        <nav className="hidden lg:flex items-center bg-white/80 backdrop-blur-md px-3 py-1.5 rounded-full border border-[#F0DCC8] shadow-xs">
          <ul className="flex items-center space-x-1 text-xs xl:text-sm font-semibold text-[#3D2E22]">
            {navLinks.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.to}
                  smooth={true}
                  duration={500}
                  offset={link.offset}
                  className="px-3.5 py-1.5 rounded-full cursor-pointer hover:text-[#D97706] hover:bg-[#FEF3E5] transition-all duration-200 block"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* ================ DESKTOP ACTION ZONE ================ */}
        <div className="hidden lg:flex items-center space-x-3">
          {userLoginToken ? (
            <DropdownUser />
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="text-xs xl:text-sm font-bold text-[#1C1410] hover:text-[#D97706] hover:bg-[#FEF3E5] px-4 py-2 rounded-full transition-all duration-200 border border-transparent hover:border-[#FDE8CD]"
              >
                Log In
              </button>

              <button
                onClick={() => navigate("/signup")}
                className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 text-xs xl:text-sm font-extrabold text-white bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C] hover:from-[#EA580C] hover:to-[#D97706] px-7 py-2.5 sm:py-3 rounded-full shadow-[0_6px_20px_rgba(217,119,6,0.35)] hover:shadow-[0_8px_25px_rgba(217,119,6,0.5)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 whitespace-nowrap shrink-0"
              >
                <span>Get Started</span>
                <FaArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200 text-white shrink-0" />
              </button>
            </>
          )}
        </div>

        {/* ================ MOBILE HAMBURGER BUTTON ================ */}
        <div className="lg:hidden flex items-center">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2.5 rounded-full text-[#1C1410] bg-[#FEF3E5] border border-[#FDE8CD] hover:bg-[#FDE8CD] transition-colors shadow-xs"
            aria-label="Toggle navigation"
          >
            {isMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>
      </div>

      {/* ================ MOBILE DRAWER & SCRIM ================ */}
      {ReactDOM.createPortal(
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-[#1C1410]/40 backdrop-blur-md z-[90] lg:hidden"
              onClick={() => setIsMenuOpen(false)}
            />

            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="fixed top-0 right-0 h-[100dvh] w-[85%] max-w-[320px] bg-[#FFFBF5] border-l border-[#F0DCC8] shadow-2xl z-[100] lg:hidden flex flex-col justify-between overflow-y-auto overscroll-contain"
              style={{ backgroundColor: "#FFFBF5" }}
            >
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between border-b border-[#F0DCC8] pb-4 mb-6">
                  <img width={120} src={logo} alt="MyAd360" className="shrink-0" />
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 rounded-full text-[#3D2E22] bg-[#FEF3E5] hover:bg-[#FDE8CD] transition-colors shrink-0"
                  >
                    <FaTimes size={16} />
                  </button>
                </div>

                <nav className="flex flex-col space-y-1.5">
                  {navLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.to}
                      smooth={true}
                      duration={500}
                      offset={link.offset}
                      onClick={() => setIsMenuOpen(false)}
                      className="px-4 py-3 rounded-xl text-sm font-bold text-[#3D2E22] hover:bg-[#FEF3E5] hover:text-[#D97706] transition-colors cursor-pointer flex items-center justify-between"
                    >
                      <span>{link.name}</span>
                      <FaChevronRight className="w-3 h-3 text-[#D97706] shrink-0" />
                    </Link>
                  ))}
                </nav>
              </div>

              {/* Mobile Auth Actions */}
              <div className="p-5 sm:p-6 border-t border-[#F0DCC8] bg-white sticky bottom-0">
                {userLoginToken ? (
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate("/user/profile");
                      }}
                      className="flex items-center justify-center gap-3 w-full px-4 py-3 text-sm font-bold text-[#1C1410] bg-white border border-[#F0DCC8] hover:bg-[#FEF3E5] rounded-full transition-colors shadow-sm"
                    >
                      <FaRegUser className="text-[#D97706]" />
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50 rounded-full transition-colors"
                    >
                      Log Out
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate("/login");
                      }}
                      className="w-full py-3 rounded-full border border-[#F0DCC8] font-bold text-[#1C1410] bg-white hover:bg-[#FEF3E5] transition-colors text-center text-sm"
                    >
                      Log In
                    </button>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate("/signup");
                      }}
                      className="w-full py-3 rounded-full bg-gradient-to-r from-[#D97706] to-[#EA580C] text-white font-extrabold text-sm shadow-[0_4px_14px_rgba(217,119,6,0.38)] hover:shadow-lg transition-all text-center flex items-center justify-center gap-2"
                    >
                      <span>Get Started</span>
                      <FaArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      , document.body)}
    </header>
  );
};

export default Header;